import type { ActionSurfaceComponent } from "@mirage/analytics/events/enums/action_surface_component";
import type { DashActionSurface } from "@mirage/analytics/events/enums/dash_action_surface";
import type { Send_ComposePrompt } from "@mirage/analytics/events/types/send_compose_prompt";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import i18n from "@mirage/translations";
import { type ConversationRouterState, CtaSource } from "../router_state_types";

/**
 * The source of the message. This is used to track the source of the message
 * for analytics purposes. Uses the ctaSource enum if the message is triggered
 * from places outside the assistant page, otherwise pass in the actionSurfaceComponent
 * to track the source within the assistant page.
 */
export function getDashActionSurfaceComponentLoggingSource(
  ctaSource: CtaSource | ActionSurfaceComponent,
) {
  const { dashActionSurface, actionSurfaceComponent } =
    getDashActionSurfaceComponentLoggingSourceParams(ctaSource);

  return `${dashActionSurface}-${actionSurfaceComponent}`;
}

export function getDashActionSurfaceComponentLoggingSourceParams(
  ctaSource: CtaSource | ActionSurfaceComponent,
) {
  let dashActionSurface: DashActionSurface;
  let actionSurfaceComponent: ActionSurfaceComponent;
  switch (ctaSource) {
    case CtaSource.OneClickAskPrompt:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "prompt_suggestion";
      break;
    case CtaSource.OmniboxAskQuery:
    case CtaSource.OmniboxAskFromSuggestion:
    case CtaSource.OmniboxDraftWithTemplate:
    case CtaSource.OmniboxDraftWithTone:
    case CtaSource.OmniboxDraftWithDocument:
    case CtaSource.OmniboxDraftWithMessage:
    case CtaSource.OmniboxRecentChat:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "omnibox";
      break;
    case CtaSource.SerpSummarize:
      dashActionSurface = "serp";
      actionSurfaceComponent = "omnibox";
      break;

    // TODO OSE-5736: logging for previews
    case CtaSource.DocumentPreviewSuggestion:
      dashActionSurface = "serp";
      actionSurfaceComponent = "prompt_suggestion";
      break;
    case CtaSource.StackChat:
      dashActionSurface = "stack_details_page";
      actionSurfaceComponent = "stack_header";
      break;
    case CtaSource.StackSuggestedPrompt:
    case CtaSource.StackSummarize:
      dashActionSurface = "stack_details_page";
      actionSurfaceComponent = "prompt_suggestion";
      break;
    case CtaSource.StackItemSummarize:
      dashActionSurface = "stack_details_page";
      actionSurfaceComponent = "stacks";
      break;
    case CtaSource.StartPageStackChat:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "stacks";
      break;
    case CtaSource.FeedItemSummarize:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "activity_feed";
      break;
    case CtaSource.RecentSummarize:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "recents";
      break;
    case CtaSource.WorkflowAgent:
      dashActionSurface = "conversation_page";
      actionSurfaceComponent = "compose_chat_pane";
      break;
    case CtaSource.LegacySession:
      dashActionSurface = "conversation_page";
      actionSurfaceComponent = "compose_chat_pane";
      break;
    case CtaSource.DraftWithTemplate:
      dashActionSurface = "conversation_page";
      actionSurfaceComponent = "assist_templates";
      break;
    // TODO: Update these to use the correct action surface component
    case CtaSource.FssUnscoped:
    case CtaSource.FssAskQuery:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "omnibox";
      break;
    case CtaSource.FssAskFromSuggestion:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "prompt_suggestion";
      break;
    case CtaSource.FssAskWithFile:
    case CtaSource.FssAskWithMultiFile:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "stacks";
      break;
    case CtaSource.FssFileSummarize:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "omnibox";
      break;
    case CtaSource.FssFolderSummarize:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "omnibox";
      break;
    case CtaSource.FssAskWithFolder:
      dashActionSurface = "start_page";
      actionSurfaceComponent = "stacks";
      break;
    case "compose_editor_pane":
      dashActionSurface = "conversation_page";
      actionSurfaceComponent = "compose_editor_pane";
      break;
    case "compose_chat_pane":
      dashActionSurface = "conversation_page";
      actionSurfaceComponent = "compose_chat_pane";
      break;
    case CtaSource.DeepLinkWrite:
    case CtaSource.DeepLinkWriteWithTemplate:
      // TODO: update these logging params once we have Bridge Logging spec.
      // see https://www.dropbox.com/scl/fi/pxy1kq0um81sc5u11nka5/Working-doc-Bridge-FSS-Dash-Logging-Spec-for-ChatAssist.paper?rlkey=jh8tqpeajpv3mkvsy6i9wuoa1&dl=0
      dashActionSurface = "start_page";
      actionSurfaceComponent = "prompt_suggestion";
      break;
    default:
      throw new Error(
        `Unexpected CTA source or action surface component: ${ctaSource}`,
      );
  }
  return { dashActionSurface, actionSurfaceComponent };
}

export const getPapEventPropsForSummarizeItem = (
  routerState: ConversationRouterState & { chatSources: ChatSource[] },
  chatSessionId: string,
  actionSurfaceComponent: ActionSurfaceComponent,
  dashActionSurface: DashActionSurface,
): Send_ComposePrompt["properties"] => {
  const papEventProps: Send_ComposePrompt["properties"] = {
    actionSurfaceComponent,
    chatSessionId,
    dashActionSurface,
    featureLine: "assist" as const,
    promptType: "summary" as const,
    queryString:
      routerState.ctaSource === CtaSource.StackSummarize
        ? i18n.t("summarize_this_stack")
        : i18n.t("summarize_documents", {
            count: routerState.chatSources.length,
          }),
  };

  // This handle SerpSummarize special case where we might have a search session id or query uuid
  if (routerState.ctaSource === CtaSource.SerpSummarize) {
    const meta = routerState.scenarioMeta;
    if (meta) {
      if ("searchSessionId" in meta && meta.searchSessionId) {
        papEventProps.searchSessionId = meta.searchSessionId;
      }
      if ("searchQueryUuid" in meta && meta.searchQueryUuid) {
        papEventProps.searchQueryUuid = meta.searchQueryUuid;
      }
    }
  }

  return papEventProps;
};
